/**
 * Base API client with common configuration and utilities
 */

/**
 * Gets the base URL for the API from environment variables
 * @returns {string} The base URL for API requests
 */
export const getBaseURL = () => {
  const productionURL = process.env.REACT_APP_STRAPI_URL;

  if (productionURL) {
    const cleanURL = productionURL.endsWith("/") ? productionURL.slice(0, -1) : productionURL;
    return cleanURL;
  }

  return "http://localhost:2010";
};

/**
 * Gets the cart session token from localStorage
 * @returns {string|null} The session token if available
 */
export const getCartSessionToken = () => {
  return localStorage.getItem("cart_session_token");
};

/**
 * Stores the cart session token in localStorage, or clears it if token is null
 * @param {string|null} token - The session token to store, or null to clear
 */
export const setCartSessionToken = (token) => {
  if (token) {
    localStorage.setItem("cart_session_token", token);
  } else {
    localStorage.removeItem("cart_session_token");
  }
};

/**
 * Generates headers for API requests
 * @param {boolean} includeContentType - Whether to include Content-Type header
 * @param {boolean} includeSessionToken - Whether to include cart session token header
 * @returns {Object} Headers object for fetch requests
 */
export const getHeaders = (includeContentType = true, includeSessionToken = false) => {
  const headers = {};

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (process.env.REACT_APP_STRAPI_API_TOKEN) {
    headers.Authorization = `Bearer ${process.env.REACT_APP_STRAPI_API_TOKEN}`;
  }

  if (includeSessionToken) {
    const sessionToken = getCartSessionToken();
    if (sessionToken) {
      headers["X-Session-Token"] = sessionToken;
    }
  }

  return headers;
};

// Base API URL with /api path
export const API_BASE_URL = getBaseURL() + "/api";

/**
 * Builds URL query string from parameters object
 * @param {Object} params - Parameters to convert to query string
 * @returns {string} URL query string
 */
export const buildQueryString = (params) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value);
    }
  });
  return queryParams.toString();
};

/**
 * Extracts and stores session token from response headers if present
 * @param {Response} response - Fetch response object
 */
const extractSessionTokenFromResponse = (response) => {
  const sessionToken = response.headers.get("X-Session-Token");
  if (sessionToken) {
    setCartSessionToken(sessionToken);
  }
};

/**
 * GET request with automatic query parameter handling and timeout support
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 * @param {Object} options - Additional fetch options
 * @param {number} options.timeout - Request timeout in milliseconds (default: 10000)
 * @param {boolean} options.includeSessionToken - Whether to include session token header
 * @returns {Promise<Response>} Fetch response
 */
export const apiGet = (endpoint, params = {}, options = {}) => {
  const { timeout = 10000, includeSessionToken = false, ...fetchOptions } = options;
  const queryString = buildQueryString(params);
  const url = `${API_BASE_URL}${endpoint}${queryString ? `?${queryString}` : ""}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(url, {
    headers: getHeaders(true, includeSessionToken),
    signal: controller.signal,
    ...fetchOptions,
  })
    .then((response) => {
      extractSessionTokenFromResponse(response);
      return response;
    })
    .finally(() => clearTimeout(timeoutId));
};

/**
 * POST request with timeout support
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data (null for no body)
 * @param {Object} options - Additional fetch options
 * @param {number} options.timeout - Request timeout in milliseconds (default: 10000)
 * @param {boolean} options.includeSessionToken - Whether to include session token header
 * @returns {Promise<Response>} Fetch response
 */
export const apiPost = (endpoint, data = null, options = {}) => {
  const { timeout = 10000, includeSessionToken = false, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers: getHeaders(true, includeSessionToken),
    body: data ? JSON.stringify(data) : null,
    signal: controller.signal,
    ...fetchOptions,
  })
    .then((response) => {
      extractSessionTokenFromResponse(response);
      return response;
    })
    .finally(() => clearTimeout(timeoutId));
};

/**
 * DELETE request with timeout support
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Additional fetch options
 * @param {number} options.timeout - Request timeout in milliseconds (default: 10000)
 * @param {boolean} options.includeSessionToken - Whether to include session token header
 * @returns {Promise<Response>} Fetch response
 */
export const apiDelete = (endpoint, options = {}) => {
  const { timeout = 10000, includeSessionToken = false, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(`${API_BASE_URL}${endpoint}`, {
    method: "DELETE",
    headers: getHeaders(true, includeSessionToken),
    signal: controller.signal,
    ...fetchOptions,
  })
    .then((response) => {
      extractSessionTokenFromResponse(response);
      return response;
    })
    .finally(() => clearTimeout(timeoutId));
};
